import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { MultiPageVideoModel } from '@bynder-studio/render-core';
import { IconTranslate } from '@bynder/icons';
import { Translate, useTranslate } from '@bynder/localization';
import {
    Box,
    Button,
    Checkbox,
    CheckboxGroup,
    InlineBanner,
    Divider,
    Form,
    Modal,
    MultiSelectChips,
    notify,
    Tooltip,
} from '@bynder/design-system';
import variationSetsService, {
    listenToTranslationStatus,
    isTerminalTranslationStatus,
} from 'packages/services/variationSetsService';
import { getSplitFeatures } from 'packages/store/user/user.selectors';
import { ActiveSplits } from 'packages/store/user/types';
import { VariationTransformedSetItemType } from 'packages/variation-export/types';
import { socketSubscribe } from 'packages/socket';
import { subscribeVariationTranslationsOptions } from 'packages/socket/subscribeOptions/variationsTranslation';
import { StyledMultiSelectChips, HiddenInput } from './Variations.styled';
import LANGS from './langs.json';
import modalContainer from 'packages/common/modalContainer';
import useAccessRights from 'packages/hooks/useAccessRights';
import features from 'packages/configs/features';
import useDesign from 'packages/pages/design/hooks/useDesign';
import { getVariationTranslationCapabilities } from './utils';

type Props = {
    onClose: () => void;
    variation: VariationTransformedSetItemType | null;
    getVariationsAndSave: () => void;
    variationsTotal: number;
};

type Language = {
    code: string;
    name: string;
    nativeName: string;
};

const preventSubmit = (event) => event.preventDefault();

const MAX_SELECTED_LANGS = 10;
const MAX_VARIATION_COUNT = 50;
const FAVORITE_LANGS = ['en', 'de', 'fr', 'es', 'it', 'nl'];

const languagesList: Language[] = Object.keys(LANGS).map((langCode) => ({
    code: langCode,
    name: LANGS[langCode].name,
    nativeName: LANGS[langCode].nativeName,
}));

const VariationTranslateModal = ({ onClose, variation, getVariationsAndSave, variationsTotal }: Props) => {
    const { translate } = useTranslate();
    const { isPlatformAllowed } = useAccessRights();
    const { creativeModel } = useDesign();
    const splitFeatures = useSelector(getSplitFeatures);
    const isSseEnabled = splitFeatures?.[ActiveSplits.studioSseSubscriptions] === 'on';
    const [selectedLngs, setSelectedLngs] = useState<string[]>([]);
    const [value, setValue] = useState('');
    const [isTranslating, setIsTranslating] = useState(false);
    // One entry per translation: the component's lifetime is the Variations sidebar tab, not the
    // dialog, so a second translation within one mount must not orphan the first stream.
    const unsubscribersRef = useRef<(() => void)[]>([]);
    const isUnmountedRef = useRef(false);

    // socketSubscribe awaits a WebSocket handshake, so a handle can land after unmount.
    const storeUnsubscriber = (unsubscribe: () => void) => {
        if (isUnmountedRef.current) {
            unsubscribe();

            return;
        }

        unsubscribersRef.current.push(unsubscribe);
    };

    useEffect(
        () => () => {
            isUnmountedRef.current = true;
            unsubscribersRef.current.forEach((unsubscribe) => unsubscribe());
            unsubscribersRef.current = [];
            // Nothing can resolve the in-progress toast once its subscription is gone.
            notify.remove('variation_translate');
        },
        [],
    );

    const isVideoDesign = creativeModel instanceof MultiPageVideoModel;
    const isTranscriptFeatureAllowed = isPlatformAllowed([features.TRANSCRIPT]) && isVideoDesign;

    // What can actually be translated for this design (editable text + enabled transcript).
    const { hasEditableTextElements, hasTranscript } = useMemo(
        () => getVariationTranslationCapabilities(isVideoDesign ? creativeModel : null),
        [creativeModel, isVideoDesign],
    );

    // Only offer an option when both the feature is enabled AND the design actually has it.
    const showTextElementsOption = isTranscriptFeatureAllowed && hasEditableTextElements;
    const showTranscriptOption = isTranscriptFeatureAllowed && hasTranscript;
    const hasTranslateOptions = showTextElementsOption || showTranscriptOption;

    // Default each checkbox to its availability (per the capabilities result).
    const [shouldTranslateTranscript, setShouldTranslateTranscript] = useState(hasTranscript);
    const [shouldTranslateTextElements, setShouldTranslateTextElements] = useState(hasEditableTextElements);

    // Disable the primary button when options exist but none of the visible ones is selected.
    const isTextElementsSelected = showTextElementsOption && shouldTranslateTextElements;
    const isTranscriptSelected = showTranscriptOption && shouldTranslateTranscript;
    const isNoTranslationOptionSelected = hasTranslateOptions && !isTextElementsSelected && !isTranscriptSelected;

    const removeLanguage = (idx: number) => {
        setSelectedLngs((prev) => {
            const temp = [...prev];
            temp.splice(idx, 1);

            return temp;
        });
    };

    const isVariationCountExceeded = selectedLngs.length + variationsTotal > MAX_VARIATION_COUNT;
    const isLangCountExceeded = selectedLngs.length > MAX_SELECTED_LANGS;

    const selectLanguage = (lng: string) => {
        if (isLangCountExceeded) {
            return;
        }

        if (selectedLngs.includes(lng)) {
            return;
        }

        setSelectedLngs((prev) => [...prev, lng]);
        setValue('');
    };

    function subscribeToUpdates(jobId: number, variationSetId: string) {
        const notifyFailure = () => {
            notify({
                title: <Translate id="design.variations.translate.failure" />,
                variant: 'error',
            });

            notify.remove('variation_translate');
        };

        const handleStatus = (status: string) => {
            if (status === 'TRANSLATED') {
                notify({
                    id: 'variation_translate',
                    title: <Translate id="design.variations.translate.success" />,
                    variant: 'success',
                });

                getVariationsAndSave();

                return;
            }

            if (isTerminalTranslationStatus(status)) {
                notifyFailure();
            }
        };

        return async () => {
            if (isSseEnabled) {
                storeUnsubscriber(
                    listenToTranslationStatus(variationSetId, jobId, {
                        onStatus: handleStatus,
                        onFailure: notifyFailure,
                    }),
                );

                return;
            }

            storeUnsubscriber(
                await socketSubscribe(
                    subscribeVariationTranslationsOptions({
                        jobId,
                        variationSetId,
                        onMessage: (translations, unsubscribe) => {
                            if (isTerminalTranslationStatus(translations.status)) {
                                unsubscribe();
                            }

                            handleStatus(translations.status);
                        },
                    }),
                ),
            );
        };
    }

    const handleTranslate = () => {
        if (!variation) {
            return;
        }

        setIsTranslating(true);

        const body: Parameters<typeof variationSetsService.translateVariation>[1] = {
            languages: selectedLngs,
        };

        // Only forward the per-content selection when the design actually offers the options;
        // otherwise omit them so the BE default (translate everything) applies.
        if (hasTranslateOptions) {
            body.translateText = isTextElementsSelected;
            body.translateSubtitles = isTranscriptSelected;
        }

        return variationSetsService
            .translateVariation(variation.id, body)
            .then((resp: any) => {
                if (resp.status !== 200) {
                    if (resp.json && resp.json.appErrorCode && resp.json.appErrorCode === 15004) {
                        notify({
                            variant: 'error',
                            isPersistent: false,
                            title: <Translate id="design.save.approval.error" />,
                        });
                    } else if (resp.json && resp.json.appErrorCode && resp.json.appErrorCode === 10500) {
                        notify({
                            title: <Translate id="design.variations.translate.limit.error" />,
                            variant: 'error',
                        });
                    } else if (resp.json && resp.json.appErrorCode && resp.json.appErrorCode === 11001) {
                        notify({
                            title: <Translate id="design.variations.translate.locked.error" />,
                            variant: 'error',
                        });
                    } else {
                        notify({
                            title: <Translate id="design.variations.translate.failure" />,
                            variant: 'error',
                        });
                    }

                    return;
                }

                const {
                    json: { jobId },
                } = resp;

                subscribeToUpdates(jobId, variation.variationSetId)();

                notify({
                    id: 'variation_translate',
                    title: <Translate id="design.variations.translate.in-progress" />,
                    variant: 'loading',
                    isPersistent: true,
                });
            })
            .finally(() => {
                setIsTranslating(false);
                onClose();
            });
    };

    const filteredLanguagesTo = useMemo(() => {
        if (value.length) {
            const query = value.toLowerCase();

            return languagesList.filter(
                ({ name, nativeName }) =>
                    name.toLowerCase().includes(query) || nativeName.toLowerCase().includes(query),
            );
        }

        return languagesList;
    }, [languagesList, value]);

    const favoriteLanguages = useMemo(() => {
        return FAVORITE_LANGS.reduce((acc, favLangCode) => {
            const lang = filteredLanguagesTo.find(({ code: langCode }) => langCode === favLangCode);

            return lang ? [...acc, lang] : acc;
        }, [] as Language[]);
    }, [filteredLanguagesTo]);

    const isLangsSame = (a: Language[], b: Language[]) => {
        if (a.length !== b.length) {
            return false;
        }

        return a.every(({ code: aLangCode }) => b.find(({ code: bLangCode }) => bLangCode === aLangCode));
    };

    useEffect(() => {
        // Chips' input is autofocused and there is no property to deprecate it. So we do it manually
        if (variation) {
            // Reset the option checkboxes to their default availability every time the modal opens.
            setShouldTranslateTextElements(hasEditableTextElements);
            setShouldTranslateTranscript(hasTranscript);

            setTimeout(() => {
                (document.activeElement as HTMLInputElement)?.blur();
                document.body.click();
            }, 0);
        }

        return () => {
            if (!variation) {
                setValue('');
                setSelectedLngs([]);
            }
        };
    }, [variation, hasEditableTextElements, hasTranscript]);

    const translateButton = () => (
        <Button
            variant="primary"
            onClick={handleTranslate}
            icon={<IconTranslate />}
            isDisabled={
                !selectedLngs.length ||
                isLangCountExceeded ||
                isVariationCountExceeded ||
                isTranslating ||
                isNoTranslationOptionSelected
            }
        >
            {translate('design.sidebar.variations.modal.translate.button.translate')}
        </Button>
    );

    return (
        <Modal
            container={modalContainer}
            isOpen={variation !== null}
            onClose={onClose}
            title={translate('design.sidebar.variations.modal.translate.title', { name: variation?.name || '' })}
            actionPrimary={
                isVariationCountExceeded ? (
                    <Tooltip content={translate('design.variations.translate.limit.info')}>{translateButton()}</Tooltip>
                ) : (
                    translateButton()
                )
            }
            actionSecondary={
                <Button variant="secondary" onClick={onClose}>
                    {translate('design.sidebar.variations.modal.translate.button.cancel')}
                </Button>
            }
        >
            <Form onSubmit={preventSubmit}>
                <Form.Group>
                    <HiddenInput type="text" value="" />
                    <Form.Label htmlFor="language_to_chips">
                        {translate('design.sidebar.variations.modal.translate.to.label')}
                    </Form.Label>
                    <StyledMultiSelectChips
                        id="language_to_chips"
                        openOn={selectedLngs.length === 0 ? 'focus' : 'type'}
                        placeholder={translate('design.sidebar.variations.modal.translate.to.placeholder')}
                        value={value}
                        disabled={isLangCountExceeded}
                        aria-describedby="language_to_chips_helper"
                        onChange={setValue}
                        onDeleteAll={() => setSelectedLngs([])}
                        chips={selectedLngs.map((lng, idx) => (
                            <MultiSelectChips.Chip key={lng} onDelete={() => removeLanguage(idx)}>
                                {lng}
                            </MultiSelectChips.Chip>
                        ))}
                    >
                        {filteredLanguagesTo.length ? (
                            <>
                                {favoriteLanguages.length && !isLangsSame(filteredLanguagesTo, favoriteLanguages) ? (
                                    <>
                                        {favoriteLanguages.map((lng) => (
                                            <MultiSelectChips.Item
                                                onClick={() => selectLanguage(lng.name)}
                                                key={lng.code}
                                                isChecked={selectedLngs.includes(lng.name)}
                                            >
                                                {lng.name}
                                            </MultiSelectChips.Item>
                                        ))}
                                        <Divider />
                                    </>
                                ) : null}
                                {filteredLanguagesTo.map((lng) => (
                                    <MultiSelectChips.Item
                                        onClick={() => selectLanguage(lng.name)}
                                        key={lng.code}
                                        isChecked={selectedLngs.includes(lng.name)}
                                    >
                                        {lng.name}
                                    </MultiSelectChips.Item>
                                ))}
                            </>
                        ) : (
                            <MultiSelectChips.Text>
                                {translate('design.sidebar.variations.modal.translate.no_result')}
                            </MultiSelectChips.Text>
                        )}
                    </StyledMultiSelectChips>
                    <Form.HelperText id="language_to_chips_helper" isInvalid={isLangCountExceeded}>
                        {translate('design.sidebar.variations.modal.translate.to.helper', {
                            count: selectedLngs.length,
                        })}
                    </Form.HelperText>
                </Form.Group>
                {showTextElementsOption && (
                    <CheckboxGroup>
                        <Checkbox
                            isChecked={shouldTranslateTextElements}
                            onChange={() => setShouldTranslateTextElements((prev) => !prev)}
                        >
                            {translate('design.sidebar.variations.modal.translate.text_elements.label')}
                        </Checkbox>
                    </CheckboxGroup>
                )}
                {showTranscriptOption && (
                    <CheckboxGroup>
                        <Checkbox
                            isChecked={shouldTranslateTranscript}
                            onChange={() => setShouldTranslateTranscript((prev) => !prev)}
                        >
                            {translate('design.sidebar.variations.modal.translate.transcript.label')}
                        </Checkbox>
                    </CheckboxGroup>
                )}
            </Form>
            <Box marginTop="4">
                <InlineBanner variant="info">
                    {translate('design.sidebar.variations.modal.translate.ai_disclaimer')}
                </InlineBanner>
            </Box>
        </Modal>
    );
};

export default VariationTranslateModal;
